import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import commonModalClass from '../assets/scss/CommonModal.module.scss';
import { OutSideCloseButton } from './ModalItem/OutSideCloseButton';

export const CommonModal: FC<{
  active: boolean;
  closeFunc?: () => void;
  loading?: boolean;
  isMaxHeight?: boolean;
  children: ReactNode;
}> = ({ active, closeFunc, loading, isMaxHeight = true, children }) => {
  const [top, setTop] = useState(-1);
  const [maxHeight, setMaxHeight] = useState(0);
  const modalBgRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef<() => void>();

  const outsideBtnHeight = 16 + 25 + 16; //マージン上16px + 画像サイズ25px + マージン下16px;

  useEffect(() => {
    if (active) {
      window.parent.postMessage({ action: 'modalSetting' }, '*');

      const fetchScrollPosition = (event: MessageEvent<any>) => {
        if (event.data.action && event.data.action === 'modalOpen') {
          const { top, maxHeight } = calcModalSetting(event, outsideBtnHeight);
          setTop(top);
          setMaxHeight(maxHeight);
          window.parent.postMessage({ action: 'fixedBtnHide' }, '*');
        }
      };

      window.addEventListener('message', fetchScrollPosition);

      return () => {
        window.removeEventListener('message', fetchScrollPosition);
        window.parent.postMessage({ action: 'fixedBtnShow' }, '*');
      };
    } else {
      setTop(-1);
      setMaxHeight(0);
    }
  }, [active]);

  useEffect(() => {
    const iframeHeight = modalBgRef.current?.clientHeight ?? 0;
    if (iframeHeight === 0) return;

    if (iframeHeight < top + outsideBtnHeight + maxHeight) {
      // はみだし対応
      setMaxHeight(iframeHeight - top - outsideBtnHeight);
    }
  }, [top, maxHeight]);

  const clickBgHandler = useCallback(
    (e) => {
      if (e.target.className !== commonModalClass.modalBg || loading) return;
      if (!closeFunc) return;

      closeFunc();
      window.parent.postMessage({ action: 'fixedBtnShow' }, '*');
    },
    [closeFunc, loading]
  );

  useEffect(() => {
    if (active && !loading) {
      callbackRef.current = closeFunc;
    } else if (typeof callbackRef.current === 'function') {
      callbackRef.current = undefined;
    }
  }, [active, closeFunc, loading]);

  useEffect(() => {
    const closeModalMessage = (event: MessageEvent) => {
      if (event.data.action === 'modalClose') {
        if (typeof callbackRef.current === 'function') {
          callbackRef.current();
          window.parent.postMessage({ action: 'fixedBtnShow' }, '*');
        }
      }
    };
    window.addEventListener('message', closeModalMessage);

    return () => window.removeEventListener('message', closeModalMessage);
  }, []);

  return (
    <div
      className={commonModalClass.modalBg}
      style={{ display: active && top !== -1 ? 'flex' : 'none' }}
      onClick={clickBgHandler}
      ref={modalBgRef}
    >
      <div
        className={commonModalClass.modalContainer}
        style={{ top: closeFunc ? `${top}px` : `${top + outsideBtnHeight}px` }}
      >
        {closeFunc && <OutSideCloseButton closeFunc={closeFunc} />}
        <div
          className={commonModalClass.modal}
          style={{
            height: isMaxHeight ? `${maxHeight}px` : 'auto',
            maxHeight: `${maxHeight}px`,
            borderRadius: isMaxHeight ? '8px 8px 0 0' : '8px',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

//表示領域最上部からの表示位置
const PC_INITIAL_TOP = 0;
const SP_INITIAL_TOP = 37;

function calcModalSetting(event: MessageEvent<any>, outsideBtnHeight: number) {
  // テーマ側から取得 (scrollY,windowHeight,windowWidth,headerHeight,iframeTop)
  const { scrollY, windowHeight, windowWidth, headerHeight, iframeTop } = formatEventData(event);

  let outsideOffset = headerHeight;
  let initialTop = PC_INITIAL_TOP;
  if (windowWidth < 767) {
    // SPはheaderが固定ではないため考慮
    outsideOffset = scrollY < headerHeight ? headerHeight - scrollY : 0;
    initialTop = SP_INITIAL_TOP < outsideOffset ? 0 : SP_INITIAL_TOP - outsideOffset;
  }

  // 表示領域最上部からの表示位置 (iframe外の高さ + 表示位置)
  const offset = outsideOffset + initialTop;
  // モーダルのtop,max-heightを設定
  const top = iframeTop - offset < scrollY ? scrollY - (iframeTop - offset) : 0;
  const maxHeight = windowHeight - outsideBtnHeight - offset;

  return { top, maxHeight };
}

// テーマ側からの値を型変換
function formatEventData(event: MessageEvent<any>) {
  return {
    scrollY: Number(event.data.scrollY),
    windowHeight: Number(event.data.windowHeight),
    windowWidth: Number(event.data.windowWidth),
    headerHeight: Number(event.data.headerHeight),
    iframeTop: Number(event.data.iframeTop),
  };
}
